import { useQuery } from 'react-query'
import { makeCoreGetRequest } from 'utils/api'
import { handleQueryError } from 'utils/helpers'
import { toEmployee, toLocationUnitsOptions } from './serializers'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { LocationSubTypeItem, SimpleItem } from 'components/location/interfaces'

export const getEmployee = (
  showNotification: Function,
  empoyeeId: number,
  shouldRefreshData?: boolean,
  setShouldRefreshData?: Function
) => {
  return useQuery(
    ['employee-item', empoyeeId, shouldRefreshData],
    async () => {
      const result = await makeCoreGetRequest(`/employee-management/person/${empoyeeId}`)
      setShouldRefreshData && setShouldRefreshData(false)

      return toEmployee(result)
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching employee.', showNotification),
      enabled: !!empoyeeId
    }
  )
}

export const getLocationTypesOptions = (showNotification: Function) => {
  return useQuery(
    ['employee-location-types'],
    async () => {
      const data = await makeCoreGetRequest('/employee-management/location-types')
      return data.map((item: SimpleItem) => ({
        label: item.name,
        value: item.id
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location types.', showNotification)
    }
  )
}

export const getLocationSubtypesOptions = (showNotification: Function, typeId: number) => {
  return useQuery(
    ['employee-location-subtypes', typeId],
    async () => {
      const data = await makeCoreGetRequest(`/employee-management/location-subtypes/${typeId}`)
      return data.map((item: LocationSubTypeItem) => ({
        label: item.name,
        value: item.id
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location subtypes.', showNotification),
      enabled: !!typeId
    }
  )
}

export const getLocationUsageOptions = (
  showNotification: Function,
  typeId: number,
  subtypeId: number,
  countyId: number,
  cityId: number
) => {
  return useQuery(
    ['location-usage-options', typeId, subtypeId, countyId, cityId],
    async () => {
      if (!typeId) return []

      const data = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/employee-management/location-usages',
          tableFilters: [
            { name: 'subtype.type.id', value: typeId },
            { name: 'subtype.id', value: subtypeId },
            { name: 'locationUsageAddress.address.city.county.id', value: countyId },
            { name: 'locationUsageAddress.address.city.id', value: cityId }
          ],
          page: -1
        })
      )

      return (
        data?.list?.map(item => ({
          label: item.name,
          value: item.code
        })) || []
      )
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location usages.', showNotification),
      enabled: !!typeId && !!subtypeId && !!countyId && !!cityId
    }
  )
}

export const getLocationUnitsOptions = (showNotification: Function, locationUsageCode: string) => {
  return useQuery(
    ['employee-location-units', locationUsageCode],
    async () => {
      const data = await makeCoreGetRequest(
        `/employee-management/location-usage-by-code/${locationUsageCode}`
      )
      return toLocationUnitsOptions(data.locationUnit)
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location subtypes.', showNotification),
      enabled: !!locationUsageCode
    }
  )
}
